import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Plus, Trash2, ListChecks } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
} from '@goodday-clone/ui'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'

// Lets a project admin define custom fields for tasks in this project — a
// first cut supporting 6 of the schema's 20 declared types. No per-project
// field editor existed before this; fields could only ever be created via
// the API directly.
const SUPPORTED_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'SELECT', label: 'Dropdown' },
  { value: 'BOOLEAN', label: 'Yes / No' },
  { value: 'USER', label: 'Person' },
]

interface CustomFieldRow {
  id: string
  _id: string
  name: string
  type: string
  config?: { options?: string[] }
}

export function CustomFieldsSettingsCard({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [type, setType] = useState('TEXT')
  const [optionsDraft, setOptionsDraft] = useState('')
  const [creating, setCreating] = useState(false)

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['custom-fields', projectId],
    queryFn: async () => {
      const res = await api.get<any>('/custom-fields', { projectId })
      return (res?.data || []) as CustomFieldRow[]
    },
    enabled: !!projectId,
  })

  const deleteField = useMutation({
    mutationFn: (id: string) => api.delete(`/custom-fields/${id}`, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fields', projectId] })
      toast.success('Custom field deleted')
    },
    onError: (err: any) => {
      toast.error(getApiErrorMessage(err, 'delete this field'))
    },
  })

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    if (type === 'SELECT') {
      const options = optionsDraft.split(',').map((o) => o.trim()).filter(Boolean)
      if (options.length === 0) {
        toast.error('Add at least one option, separated by commas')
        return
      }
    }
    setCreating(true)
    try {
      const config = type === 'SELECT'
        ? { options: optionsDraft.split(',').map((o) => o.trim()).filter(Boolean) }
        : undefined
      await api.post('/custom-fields', { name: name.trim(), type, projectId, config }, { skipErrorToast: true })
      setName('')
      setOptionsDraft('')
      setType('TEXT')
      queryClient.invalidateQueries({ queryKey: ['custom-fields', projectId] })
      toast.success('Custom field created')
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'create custom fields'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-primary" />
          Custom Fields
        </CardTitle>
        <CardDescription className="text-xs">
          Extra fields available on tasks in this project
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading...</p>
        ) : fields.length === 0 ? (
          <p className="text-xs text-muted-foreground">No custom fields yet.</p>
        ) : (
          <div className="space-y-2">
            {fields.map((f) => (
              <div
                key={f.id || f._id}
                className="flex items-center justify-between rounded-lg border border-border/60 p-2.5"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{f.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {SUPPORTED_TYPES.find((t) => t.value === f.type)?.label || f.type}
                    {f.config?.options && f.config.options.length > 0 && ` — ${f.config.options.join(', ')}`}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteField.mutate(f.id || f._id)}
                  disabled={deleteField.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-border/60">
          <div className="flex-1 space-y-1">
            <Label htmlFor="newFieldName" className="text-xs">Field name</Label>
            <Input
              id="newFieldName"
              placeholder="e.g. Client Tier"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="w-32 space-y-1">
            <Label htmlFor="newFieldType" className="text-xs">Type</Label>
            <select
              id="newFieldType"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {SUPPORTED_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {type === 'SELECT' && (
            <div className="flex-1 space-y-1">
              <Label htmlFor="newFieldOptions" className="text-xs">Options (comma-separated)</Label>
              <Input
                id="newFieldOptions"
                placeholder="Gold, Silver, Bronze"
                value={optionsDraft}
                onChange={(e) => setOptionsDraft(e.target.value)}
              />
            </div>
          )}
          <div className="flex items-end">
            <Button type="submit" size="sm" disabled={creating || !name.trim()} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
